import passengersBuilder from '../../shared/builders/passengersBuilder';
import trimISODate from '../../shared/dates/trimISODate';
import serializeIsoDateTime from '../../shared/dates/serializeIsoDateTime';
import { DEFAULT_LOCALE, matchLocale, SupportedLocale } from '../../../translations/locales';
import { DefaultQueryParams } from '../../shared/types/DefaultQueryParams';
import { ExtraPartnerParams, DeepLinkPayload } from '../../shared/types/DeepLinkPayload';
import { warnOfDeprecations } from './deprecations';
import {
  DEFAULT_CURRENCY,
  DEFAULT_DEP_POS_ID,
  DEFAULT_RET_DATE,
  DEFAULT_SITE_LOCATION,
  DEFAULT_TAR_POS_ID,
} from './fallbacks';

function extractPartnerParameters(data: DefaultQueryParams): ExtraPartnerParams {
  const extra: ExtraPartnerParams = {};

  if (typeof data.extra_sortingOrder === 'string') {
    extra.sortingOrder = data.extra_sortingOrder;
  }

  if (typeof data.extra_map === 'boolean') {
    extra.map = data.extra_map;
  }

  if (typeof data.extra_hotelId === 'number') {
    extra.hotelId = data.extra_hotelId.toString();
  }

  if (
    typeof data.extra_propertyTypes === 'string' ||
    typeof data.extra_propertyTypes === 'number'
  ) {
    extra.propertyTypes = data.extra_propertyTypes.toString();
  }

  if (typeof data.extra_filters === 'string') {
    extra.filters = data.extra_filters;
  }

  return extra;
}

function mergeDeprecatedParametersIntoExtra(
  deprecatedSortingOrder: string,
  deprecatedEntityId: number | string,
  extra: ExtraPartnerParams,
): ExtraPartnerParams {
  const newExtra = { ...extra };
  if (extra.sortingOrder === undefined && deprecatedSortingOrder !== '') {
    newExtra.sortingOrder = deprecatedSortingOrder.toString();
  }
  if (extra.hotelId === undefined && deprecatedEntityId !== '') {
    newExtra.hotelId = deprecatedEntityId.toString();
  }
  return newExtra;
}

export type BuildDeepLinkSystemPayloadOptions = {
  locale?: SupportedLocale;
  now?: Date;
  clientId?: string;
};

function buildDeepLinkSystemPayload(
  data: DefaultQueryParams,
  {
    locale: fallbackLocale = DEFAULT_LOCALE,
    now = new Date(),
  }: BuildDeepLinkSystemPayloadOptions = {},
): DeepLinkPayload {
  const passengerAges = passengersBuilder(data.passengerAges);

  const {
    cur = '',
    cmp = '',
    date_from = '',
    entityId = '',
    position_id = '',
    sortingOrder = '',
  } = data;

  warnOfDeprecations(data);

  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const DEFAULT_DEP_DATE = serializeIsoDateTime(tomorrow);
  const departureDate = data.depDate || date_from || DEFAULT_DEP_DATE;
  const returnDate = data.retDate || DEFAULT_RET_DATE;

  const {
    currency = cur || DEFAULT_CURRENCY,
    depPosId = DEFAULT_DEP_POS_ID,
    locale: requestedLocale,
    siteLoc = cmp || DEFAULT_SITE_LOCATION,
    platform = 'Unknown',
    component = 'Unknown',
    abVariants = data.abVariants || '',
    tarPosId = position_id || DEFAULT_TAR_POS_ID,
    redirectUrl,
    partner,
    labelField,
    campaign,
    roomsAmount,
    clientId = data.clientId ?? '',
  } = data;
  /* TEMPORARY REDUNDANCY */
  const extra = mergeDeprecatedParametersIntoExtra(
    sortingOrder,
    entityId,
    extractPartnerParameters(data),
  );

  const arrivalAtTargetLocation = trimISODate(departureDate);
  const departureFromTargetLocation = returnDate && trimISODate(returnDate);
  const locale = requestedLocale ? matchLocale(requestedLocale) : fallbackLocale;

  const payload: DeepLinkPayload = {
    arrivalId: tarPosId.toString(),
    currency,
    departureDate: arrivalAtTargetLocation,
    departureId: depPosId.toString(),
    extra,
    locale,
    passengerAges,
    returnDate: departureFromTargetLocation,
    siteLocation: siteLoc,
    platform,
    component,
    abVariants,
    redirectUrl,
    partner,
    labelField,
    campaign,
    roomsAmount: roomsAmount?.toString(),
    clientId,
  };

  return payload;
}

export default buildDeepLinkSystemPayload;
